import { __spreadArray, __assign } from 'tslib';
import { useEffect, useCallback, useState } from 'react';
import { KpkApiResponseUtils } from '@keepeek/api-client';
import { useSheetFields, useLookup, customerCommonOverrideAtom, customSelectorInOut, FunctionOverrideKey } from '@keepeek/commons';
import Axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import { useRecoilState, useSetRecoilState, useRecoilValueLoadable, useRecoilValue } from 'recoil';
import { updateLoadingState, addTreeOption } from '../fields/tree/utils.js';
import { formFieldsAtom, formFieldThesaurusOptionsToLoadState } from './atoms.js';
import { formFieldThesaurusOptionsSelector } from './selectors.js';
import { buildFormField, adaptThesaurusApiResult, getAutocompleteResults } from './utils.js';

function useFormFields(_a) {
    var internalFieldNames = _a.internalFieldNames, contextKey = _a.contextKey;
    var _b = useRecoilState(formFieldsAtom(contextKey)), formFields = _b[0], setFormFields = _b[1];
    var _c = useSheetFields(), loading = _c.loading, sheetFields = _c.sheetFields;
    useEffect(function () {
        if (!loading && sheetFields) {
            var formFieldsToBuild_1 = [];
            internalFieldNames.forEach(function (internalName) {
                var _a;
                var sheetField = sheetFields.find(function (sheetField) { return sheetField.id === internalName.field; });
                if (sheetField) {
                    var formField = buildFormField(sheetField, ((_a = internalName.thesaurusSettings) === null || _a === void 0 ? void 0 : _a.treeViewByDefault) || false, internalName.required);
                    if (formField) {
                        formFieldsToBuild_1.push(formField);
                    }
                }
            });
            setFormFields(formFieldsToBuild_1);
        }
    }, [internalFieldNames, loading, setFormFields, sheetFields]);
    return { formFields: formFields };
}
function useFormField(_a) {
    var contextKey = _a.contextKey, formFieldId = _a.formFieldId;
    var setFormFields = useSetRecoilState(formFieldsAtom(contextKey));
    var updateValue = useCallback(function (newValue) {
        setFormFields(function (old) { return __spreadArray([], old.map(function (o) {
            return __assign(__assign({}, o), (o.id === formFieldId && { value: newValue }));
        }), true); });
    }, [formFieldId, setFormFields]);
    return {
        updateValue: updateValue
    };
}
var useFieldListContainer = function (_a) {
    var _b, _c, _d;
    var formField = _a.formField;
    var sheetFields = useSheetFields().sheetFields;
    var options = [];
    var lookupId = Number(KpkApiResponseUtils.extractEntityIdFromHref((_c = (_b = sheetFields === null || sheetFields === void 0 ? void 0 : sheetFields.find(function (s) { return s.id === formField.id; })) === null || _b === void 0 ? void 0 : _b._links) === null || _c === void 0 ? void 0 : _c["kpk:lookup"].href)) || 0;
    var _e = useLookup({ lookupId: lookupId }), lookup = _e.lookup, loading = _e.loading;
    if (!loading && lookup) {
        options =
            ((_d = lookup._embedded) === null || _d === void 0 ? void 0 : _d.value.map(function (l) {
                return {
                    value: l.value || "",
                    label: l.label || ""
                };
            })) || [];
    }
    return {
        options: options
    };
};
var useFormFieldThesaurusContainer = function (_a) {
    var _b, _c;
    var formField = _a.formField;
    var sheetFields = useSheetFields().sheetFields;
    var initialThesaurusTreeId = Number(KpkApiResponseUtils.extractEntityIdFromHref((_c = (_b = sheetFields === null || sheetFields === void 0 ? void 0 : sheetFields.find(function (s) { return s.id === formField.id; })) === null || _b === void 0 ? void 0 : _b._links) === null || _c === void 0 ? void 0 : _c["kpk:thesaurus"].href)) || 0;
    var _d = useRecoilState(formFieldThesaurusOptionsToLoadState(formField.id)), formFieldThesaurusOptionsToLoad = _d[0], setFormFieldThesaurusOptionsToLoad = _d[1];
    var formFieldThesaurusOptionsLoadable = useRecoilValueLoadable(formFieldThesaurusOptionsSelector(formField.id));
    var loadOptionsForThesaurusId = function (thesaurusId) {
        setFormFieldThesaurusOptionsToLoad(thesaurusId);
    };
    var _e = useState([]), options = _e[0], setOptions = _e[1];
    var _f = useState([]), autocompleteOptions = _f[0], setAutocompleteOptions = _f[1];
    var customerCommonOverride = useRecoilValue(customerCommonOverrideAtom);
    useEffect(function () {
        setFormFieldThesaurusOptionsToLoad(initialThesaurusTreeId);
    }, [initialThesaurusTreeId, setFormFieldThesaurusOptionsToLoad]);
    useEffect(function () {
        var _a;
        if (formFieldThesaurusOptionsLoadable.state === "hasValue" &&
            formFieldThesaurusOptionsLoadable.contents) {
            var apiTreeResult = formFieldThesaurusOptionsLoadable.contents;
            var treeResultId_1 = Number(KpkApiResponseUtils.extractEntityIdFromHref((_a = apiTreeResult._links) === null || _a === void 0 ? void 0 : _a["kpk:thesaurus"].href)) || 0;
            setOptions(function (oldOptions) {
                var clonedOptions = cloneDeep(oldOptions);
                updateLoadingState(clonedOptions, undefined, false);
                return addTreeOption(treeResultId_1, clonedOptions, adaptThesaurusApiResult(formFieldThesaurusOptionsLoadable.contents));
            });
        }
        else if (formFieldThesaurusOptionsLoadable.state === "loading") {
            setOptions(function (oldOptions) {
                var clonedOptions = cloneDeep(oldOptions);
                updateLoadingState(clonedOptions, formFieldThesaurusOptionsToLoad, true);
                return clonedOptions;
            });
        }
    }, [formFieldThesaurusOptionsLoadable, formFieldThesaurusOptionsToLoad]);
    var autocompleteSearch = function (searchQuery) {
        getAutocompleteResults(Axios, searchQuery, initialThesaurusTreeId).then(function (r) {
            return setAutocompleteOptions(r);
        });
    };
    var getOptionsWithPotentialOverride = function (options) {
        var treeOption = customSelectorInOut(FunctionOverrideKey.ComponentThesaurusOptionsOut, {
            internalFieldName: formField === null || formField === void 0 ? void 0 : formField.id,
            treeOption: options
        }, customerCommonOverride).treeOption;
        return treeOption;
    };
    return {
        autocompleteSearch: autocompleteSearch,
        loadOptionsForThesaurusId: loadOptionsForThesaurusId,
        options: getOptionsWithPotentialOverride(options),
        autocompleteOptions: autocompleteOptions
    };
};

export { useFieldListContainer, useFormField, useFormFieldThesaurusContainer, useFormFields };
//# sourceMappingURL=hooks.js.map
